import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import {IconButton} from '@mui/material';
import {useEffect, useLayoutEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Outlet, useLocation, useNavigate} from 'react-router-dom';
import {
    resetSheetHeader,
    SheetHeaderState,
} from '../redux/sheetHeader/sheetHeaderSlice';
import {setHeaderTitle} from '../redux/headerTitle/headerTitleSlice';
import {RootState} from '../redux/store';
import {ArrowBack, ShareAlt} from '@kaman/uikit';

const SheetLayout = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {pathname, search} = useLocation();
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const sheetHeader = useSelector<RootState, SheetHeaderState>(
        (state) => state.sheetHeader
    );

    const OVERLAP_OFFSET = '8svh';
    const basePadding = sheetHeader.contentPadding ?? '20px 14px';

    useLayoutEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [pathname, search]);

    useEffect(() => {
        dispatch(
            setHeaderTitle({
                header: '',
                color: 'var(--header-color)',
                parentRoute: null,
                display: 'none',
            })
        );
        return () => {
            dispatch(resetSheetHeader());
            dispatch(
                setHeaderTitle({
                    header: '',
                    color: 'var(--header-color)',
                    parentRoute: null,
                    display: null,
                })
            );
        };
    }, [dispatch]);

    const handleShare = async () => {
        const url = window.location.href;
        if (navigator.share) {
            try {
                await navigator.share({
                    title: sheetHeader.title || document.title,
                    url,
                });
            } catch {
                /* ignore */
            }
        } else {
            try {
                await navigator.clipboard.writeText(url);
            } catch {
                /* ignore */
            }
        }
    };

    const handleBackOrClose = () => {
        if (sheetHeader.showBack) {
            const canGoBack = window.history.length > 1;
            if (canGoBack) {
                navigate(-1);
            } else {
                navigate('/home', {replace: true});
            }
        } else {
            navigate('/home', {replace: true});
        }
    };

    return (
        <div
            style={{
                height: '100dvh',
                maxHeight: 'calc(100dvh - 60px)',
                background: [
                    `url(${sheetHeader.headerBackgroundImage}) right -30px top / auto no-repeat`,
                    sheetHeader.headerBackground,
                ].join(', '),
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'hidden',
                overflowX: 'hidden',
            }}
        >
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                    padding: '16px 12px',
                    display: 'flex',
                    alignItems: 'start',
                    justifyContent: 'space-between',
                    color: sheetHeader.headerTextColor,
                    flex: '0 0 auto',
                    height:
                        sheetHeader.headerHeight !== 'auto'
                            ? sheetHeader.headerHeight
                            : undefined,
                    minHeight:
                        sheetHeader.headerHeight === 'auto'
                            ? '64px'
                            : sheetHeader.headerHeight,
                }}
            >
                <div>
                    {sheetHeader.showBack ? (
                        <IconButton
                            sx={{color: sheetHeader.headerTextColor}}
                            size="small"
                            onClick={handleBackOrClose}
                            aria-label="back"
                        >
                            <ArrowBack size={28} />
                        </IconButton>
                    ) : null}
                </div>

                <div
                    style={{
                        fontWeight: 700,
                        fontSize: '16px',
                        textAlign: 'center',
                        flex: 1,
                        margin: '0 8px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {sheetHeader.title}
                </div>

                <div>
                    {sheetHeader.showShare ? (
                        <IconButton
                            sx={{color: sheetHeader.headerTextColor}}
                            size="small"
                            onClick={handleShare}
                            aria-label="share"
                        >
                            <ShareAlt size={28} />
                        </IconButton>
                    ) : null}
                </div>
            </div>

            <div
                ref={scrollContainerRef}
                style={{
                    flex: 1,
                    minHeight: 0,
                    marginTop: sheetHeader.contentOverlap
                        ? `-${OVERLAP_OFFSET}`
                        : undefined,
                    paddingTop: sheetHeader.contentOverlap
                        ? OVERLAP_OFFSET
                        : undefined,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    background: 'transparent',
                }}
            >
                <div
                    style={{
                        minHeight: '100%',
                        background: '#FFFFFF',
                        borderTopLeftRadius: '24px',
                        borderTopRightRadius: '24px',
                        padding: basePadding,
                        position: 'relative',
                        zIndex: sheetHeader.contentOverlap ? 20 : undefined,
                    }}
                >
                    <Outlet />
                </div>
            </div>
        </div>
    );
};

export default SheetLayout;
